import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { parseCsv } from '../lib/csvParser';
import {
  buildReport,
  filterSales,
  aggregateFromSales,
  computePriorRange,
  pickGranularity,
} from '../lib/etsyReportEngine';
import { exportReportToExcel, exportReportAsImage } from '../lib/excelExport';

const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'AU$' };
const DAY_MS = 24 * 60 * 60 * 1000;
const DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']; // for compact chips
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKENDS = [0, 6];

function fmt(v, sym = '$') {
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 10000) return `${sign}${sym}${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${sign}${sym}${abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function fmtDate(d) {
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateShort(d) {
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function toIsoDate(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Build the canonical ranges available as presets, anchored to a reference date. */
function buildPresets(dataStart, dataEnd) {
  if (!dataEnd) return [];
  const ref = endOfDay(dataEnd);

  const lastN = (n) => ({
    start: startOfDay(new Date(ref.getTime() - (n - 1) * DAY_MS)),
    end: ref,
  });

  // This calendar week: Monday → min(Sunday, dataEnd). Anchored to dataEnd's week.
  const dow = ref.getDay() || 7; // Sun=7
  const monday = startOfDay(new Date(ref.getTime() - (dow - 1) * DAY_MS));
  const sundayCap = endOfDay(new Date(monday.getTime() + 6 * DAY_MS));
  const thisWeekEnd = sundayCap.getTime() > ref.getTime() ? ref : sundayCap;

  // This calendar month of ref
  const thisMonthStart = startOfDay(new Date(ref.getFullYear(), ref.getMonth(), 1));
  const thisMonthEnd = endOfDay(new Date(ref.getFullYear(), ref.getMonth() + 1, 0));

  // Last calendar month
  const lastMonthStart = startOfDay(new Date(ref.getFullYear(), ref.getMonth() - 1, 1));
  const lastMonthEnd = endOfDay(new Date(ref.getFullYear(), ref.getMonth(), 0));

  // This calendar quarter
  const q = Math.floor(ref.getMonth() / 3);
  const thisQuarterStart = startOfDay(new Date(ref.getFullYear(), q * 3, 1));
  const thisQuarterEnd = endOfDay(new Date(ref.getFullYear(), q * 3 + 3, 0));

  // YTD
  const ytdStart = startOfDay(new Date(ref.getFullYear(), 0, 1));
  const ytdEnd = ref;

  // Holiday: Nov 1 → Dec 31 of relevant year (use ref's year if past Nov, else prior year)
  const holidayYear = ref.getMonth() >= 10 ? ref.getFullYear() : ref.getFullYear() - 1;
  const holidayStart = startOfDay(new Date(holidayYear, 10, 1));
  const holidayEnd = endOfDay(new Date(holidayYear, 11, 31));

  return [
    { preset: 'all',         label: 'All time',     start: null, end: null },
    { preset: 'last7d',      label: 'Last 7d',      ...lastN(7) },
    { preset: 'last30d',     label: 'Last 30d',     ...lastN(30) },
    { preset: 'last90d',     label: 'Last 90d',     ...lastN(90) },
    { preset: 'thisWeek',    label: 'This week',    start: monday, end: thisWeekEnd },
    { preset: 'thisMonth',   label: 'This month',   start: thisMonthStart, end: thisMonthEnd },
    { preset: 'lastMonth',   label: 'Last month',   start: lastMonthStart, end: lastMonthEnd },
    { preset: 'thisQuarter', label: 'This quarter', start: thisQuarterStart, end: thisQuarterEnd },
    { preset: 'ytd',         label: 'YTD',          start: ytdStart, end: ytdEnd },
    { preset: 'holiday',     label: `Holiday ${String(holidayYear).slice(2)}`, start: holidayStart, end: holidayEnd },
  ];
}

/** Sanitize a label for use in a filename. */
function safeFileLabel(label) {
  return (label || '').replace(/[^\w\d-]+/g, '-').replace(/^-+|-+$/g, '') || 'All-time';
}

/** Title-case label for a granularity, used in the export checkbox. */
function trendLabelFor(g) {
  if (g === 'day') return 'Daily';
  if (g === 'week') return 'Weekly';
  if (g === 'quarter') return 'Quarterly';
  return 'Monthly';
}

/** Compute pct-change delta given current and prior values. Returns { pct, dir } or null. */
function computeDelta(current, prior) {
  if (!Number.isFinite(current) || !Number.isFinite(prior)) return null;
  if (prior === 0 && current === 0) return { pct: 0, dir: 'flat', abs: 0 };
  if (prior === 0) return { pct: null, dir: current > 0 ? 'up' : 'down', abs: current - prior };
  const pct = ((current - prior) / Math.abs(prior)) * 100;
  return {
    pct,
    dir: pct > 0.5 ? 'up' : pct < -0.5 ? 'down' : 'flat',
    abs: current - prior,
  };
}

export default function ReportsPanel({ settings }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeSummary: true,
    includeMonthly: true,
    includeProducts: true,
    includeAllSales: true,
    includeInsights: true,
  });
  const [exportStatus, setExportStatus] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // ── Time-filter state ───────────────────────────────────────────
  // range: { preset, label, start: Date|null, end: Date|null }
  const [range, setRange] = useState({ preset: 'all', label: 'All time', start: null, end: null });
  const [granularity, setGranularity] = useState('month');
  const [granularityAuto, setGranularityAuto] = useState(true); // auto-pick until user manually toggles
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [compare, setCompare] = useState(false);
  const [daysOfWeek, setDaysOfWeek] = useState(ALL_DAYS);   // [] is "none" — UI guards against this
  // exportGranularity: optional override of `granularity` just at export time.
  // null means "follow the panel".
  const [exportGranularity, setExportGranularity] = useState(null);

  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setErrorMsg('');
    setLoading(true);
    setFileName(file.name);
    try {
      const text = await file.text();
      const { rows } = parseCsv(text);
      if (rows.length === 0) {
        throw new Error('No data rows found. Make sure this is an Etsy "Sold Orders" CSV export.');
      }
      const built = buildReport(rows, settings || {});
      if (!built.summary) {
        throw new Error(built.meta?.errors?.[0] || 'Could not build report from this CSV.');
      }
      setReport(built);
      // Reset filter state to "All time" on a new upload
      setRange({ preset: 'all', label: 'All time', start: null, end: null });
      setGranularity('month');
      setGranularityAuto(true);
      setCompare(false);
      setCustomStart('');
      setCustomEnd('');
      setProductSearch('');
      setDaysOfWeek(ALL_DAYS);
      setExportGranularity(null);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to parse CSV.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [settings]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleReset = () => {
    setReport(null);
    setFileName('');
    setErrorMsg('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Build presets from the data's actual date range
  const presets = useMemo(() => {
    if (!report?.summary?.dateEnd) return [];
    return buildPresets(report.summary.dateStart, report.summary.dateEnd);
  }, [report]);

  // ── Filtered/aggregated report (the actual data shown) ─────────
  const view = useMemo(() => {
    if (!report || !report.sales) return null;
    const filteredSales = filterSales(report.sales, range, daysOfWeek);
    return aggregateFromSales(filteredSales, settings || {}, { granularity });
  }, [report, range, daysOfWeek, granularity, settings]);

  // ── Prior-period comparison report (only when toggled on) ──────
  const priorView = useMemo(() => {
    if (!report || !compare || range.preset === 'all') return null;
    const priorRange = computePriorRange(range);
    if (!priorRange) return null;
    const priorSales = filterSales(report.sales, priorRange, daysOfWeek);
    if (priorSales.length === 0) return null;
    const agg = aggregateFromSales(priorSales, settings || {}, { granularity, skipInsights: true });
    return { ...agg, range: priorRange };
  }, [report, compare, range, daysOfWeek, granularity, settings]);

  // ── Auto-pick granularity when range changes (unless user overrode) ──
  useEffect(() => {
    if (!granularityAuto) return;
    if (!view?.summary?.dateStart || !view?.summary?.dateEnd) return;
    const suggested = pickGranularity({
      start: view.summary.dateStart,
      end: view.summary.dateEnd,
    });
    if (suggested !== granularity) setGranularity(suggested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, view?.summary?.dateStart, view?.summary?.dateEnd, granularityAuto]);

  const sym = view?.summary?.currency
    ? (CURRENCY_SYMBOLS[view.summary.currency] || view.summary.currency + ' ')
    : '$';

  const handleExcelExport = async () => {
    setExportStatus('Generating Excel file…');
    try {
      // If user picked a different granularity for the export than what's on screen,
      // re-aggregate the filtered sales at that granularity so the trend sheet matches.
      const effectiveGranularity = exportGranularity || granularity;
      let exportView = view;
      if (effectiveGranularity !== granularity) {
        const filteredSales = filterSales(report.sales, range, daysOfWeek);
        exportView = aggregateFromSales(filteredSales, settings || {}, { granularity: effectiveGranularity });
      }
      const filterMeta = {
        rangeLabel: range.label,
        rangeStart: exportView?.summary?.dateStart,
        rangeEnd: exportView?.summary?.dateEnd,
        granularity: effectiveGranularity,
        daysOfWeek: daysOfWeek.length < 7 ? daysOfWeek : null,
      };
      const safeLabel = safeFileLabel(range.label);
      const dowSuffix = daysOfWeek.length < 7
        ? '_' + daysOfWeek.slice().sort().map(i => DOW_NAMES[i].slice(0, 2)).join('')
        : '';
      const granSuffix = effectiveGranularity !== 'month' ? `_${effectiveGranularity}` : '';
      const baseName = `EtsyReport_${safeLabel}${dowSuffix}${granSuffix}`;
      const { sheetCount } = await exportReportToExcel(exportView, exportOptions, baseName, filterMeta);
      setExportStatus(`✓ Downloaded with ${sheetCount} sheet${sheetCount === 1 ? '' : 's'}`);
      setTimeout(() => setExportStatus(''), 3000);
    } catch (err) {
      setExportStatus(`⚠️ ${err.message}`);
      setTimeout(() => setExportStatus(''), 5000);
    }
  };

  const handleImageExport = async () => {
    setExportStatus('Capturing report as image…');
    try {
      const safeLabel = safeFileLabel(range.label);
      await exportReportAsImage('etsy-report-content', `EtsyReport_${safeLabel}`);
      setExportStatus('✓ Image downloaded');
      setTimeout(() => setExportStatus(''), 3000);
    } catch (err) {
      setExportStatus(`⚠️ ${err.message}`);
      setTimeout(() => setExportStatus(''), 5000);
    }
  };

  const toggleExportOption = (key) => {
    setExportOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const setRangeFromPreset = (p) => {
    setRange({ preset: p.preset, label: p.label, start: p.start, end: p.end });
    setGranularityAuto(true);
  };

  const applyCustomRange = () => {
    if (!customStart || !customEnd) return;
    const start = startOfDay(new Date(customStart + 'T00:00:00'));
    const end = endOfDay(new Date(customEnd + 'T00:00:00'));
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;
    if (start > end) return;
    const label = `${fmtDateShort(start)} – ${fmtDateShort(end)}`;
    setRange({ preset: 'custom', label, start, end });
    setGranularityAuto(true);
  };

  // ─── DROP ZONE VIEW ─────────────────────────────────────────────
  if (!report) {
    return (
      <div className="sketch-card">
        <h2 className="font-display text-2xl text-ink mb-1">📊 Sales Report</h2>
        <p className="font-body text-ink/82 mb-4" style={{ fontSize: '1rem' }}>
          Upload your Etsy sales export and get a full profit dashboard — top products, monthly trends, refund analysis, and actionable insights. All processing happens in your browser; nothing is uploaded to any server.
        </p>

        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2.5px dashed ${isDragging ? '#1e3a8a' : 'rgba(26, 26, 46, 0.25)'}`,
            borderRadius: '0.875rem',
            padding: '2.5rem 1.5rem',
            textAlign: 'center',
            background: isDragging ? 'rgba(30, 58, 138, 0.04)' : 'rgba(180, 83, 9, 0.025)',
            cursor: 'pointer',
            transition: 'all 0.15s',
            marginBottom: '1.25rem',
          }}
        >
          <div style={{ fontSize: '2.4rem', marginBottom: '0.5rem' }} aria-hidden="true">
            {loading ? '⏳' : '📥'}
          </div>
          {loading ? (
            <p className="font-body" style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1a1a2e' }}>
              Parsing {fileName}…
            </p>
          ) : (
            <>
              <p className="font-body text-ink" style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.4rem' }}>
                Drop your Etsy <code style={{ background: 'rgba(26,26,46,0.06)', padding: '0.1rem 0.35rem', borderRadius: '0.25rem', fontSize: '0.95rem' }}>EtsySoldOrders.csv</code> here
              </p>
              <p className="font-body text-ink/78" style={{ fontSize: '0.92rem', marginBottom: '0.75rem' }}>
                or click to browse · processed entirely in your browser
              </p>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                style={{
                  padding: '0.5rem 1.1rem',
                  background: '#1e3a8a',
                  color: '#fefdf8',
                  border: '2px solid #1a1a2e',
                  borderRadius: '0.5rem',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.93rem',
                  cursor: 'pointer',
                  boxShadow: '2px 3px 0 rgba(26, 26, 46, 0.18)',
                }}
              >
                Choose CSV file
              </button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </div>

        {errorMsg && (
          <div style={{
            padding: '0.75rem 1rem',
            background: 'rgba(159, 18, 57, 0.06)',
            border: '1.5px solid rgba(159, 18, 57, 0.3)',
            borderRadius: '0.55rem',
            marginBottom: '1.25rem',
          }}>
            <p className="font-body" style={{ fontSize: '0.93rem', color: '#9f1239', fontWeight: 600 }}>
              ⚠️ {errorMsg}
            </p>
          </div>
        )}

        <details style={{
          background: 'rgba(30, 58, 138, 0.04)',
          border: '1px solid rgba(30, 58, 138, 0.2)',
          borderRadius: '0.55rem',
          padding: '0.75rem 1rem',
        }}>
          <summary style={{
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.95rem',
            fontWeight: 600,
            color: '#1e3a8a',
          }}>
            How do I get this CSV from Etsy?
          </summary>
          <ol style={{
            marginTop: '0.75rem',
            paddingLeft: '1.4rem',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.93rem',
            color: 'rgba(26,26,46,0.85)',
            lineHeight: 1.7,
          }}>
            <li>Sign in to Etsy.com and open <strong>Shop Manager</strong></li>
            <li>Go to <strong>Settings → Options</strong></li>
            <li>Click the <strong>Download Data</strong> tab</li>
            <li>Pick a year (or "All time"), then under <strong>"Sold Orders"</strong> click <strong>Download CSV</strong></li>
            <li>Drop the file here — done.</li>
          </ol>
          <p style={{
            marginTop: '0.5rem',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.85rem',
            color: 'rgba(26,26,46,0.7)',
          }}>
            Your data never leaves your browser. We don't store, log, or transmit any of it.
          </p>
        </details>
      </div>
    );
  }

  // ─── REPORT VIEW ────────────────────────────────────────────────
  // If filter produced an empty subset, show a friendly empty state with reset
  if (!view?.summary) {
    return (
      <div className="sketch-card">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 className="font-display text-2xl text-ink">📊 Sales Report</h2>
            <p className="font-body text-ink/78" style={{ fontSize: '0.93rem' }}>
              {fmtDate(report.summary.dateStart)} — {fmtDate(report.summary.dateEnd)} · {report.summary.validSaleCount} sales loaded
            </p>
          </div>
          <button
            onClick={handleReset}
            style={resetBtnStyle()}
          >
            ↻ New
          </button>
        </div>

        <FilterBar
          presets={presets}
          range={range}
          setRangeFromPreset={setRangeFromPreset}
          customStart={customStart}
          customEnd={customEnd}
          setCustomStart={setCustomStart}
          setCustomEnd={setCustomEnd}
          applyCustomRange={applyCustomRange}
          dataMin={toIsoDate(report.summary.dateStart)}
          dataMax={toIsoDate(report.summary.dateEnd)}
          daysOfWeek={daysOfWeek}
          setDaysOfWeek={setDaysOfWeek}
        />

        <div style={{
          padding: '2.5rem 1rem',
          textAlign: 'center',
          background: 'rgba(180, 83, 9, 0.05)',
          border: '1.5px dashed rgba(180, 83, 9, 0.3)',
          borderRadius: '0.55rem',
          marginTop: '1rem',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>🗓️</div>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.3rem' }}>
            No sales in the selected range
          </p>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: 'rgba(26,26,46,0.7)' }}>
            Pick another preset above, or switch back to "All time."
          </p>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line no-unused-vars
  const { summary, byBucket, byMonth, topProducts, insights } = view;
  const maxBucketRevenue = Math.max(...byBucket.map(m => m.revenue), 1);

  const productsWithRank = topProducts.map((p, i) => ({ ...p, originalRank: i + 1 }));
  const filteredProducts = productSearch.trim()
    ? productsWithRank.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase().trim()))
    : productsWithRank;
  const anyRefunds = topProducts.some(p => p.refundCount > 0);

  // Deltas vs prior period
  const deltas = priorView?.summary ? {
    revenue: computeDelta(summary.totalRevenue, priorView.summary.totalRevenue),
    fees:    computeDelta(summary.totalFees, priorView.summary.totalFees),
    profit:  computeDelta(summary.totalProfit, priorView.summary.totalProfit),
    aov:     computeDelta(summary.avgOrderValue, priorView.summary.avgOrderValue),
    orders:  computeDelta(summary.validSaleCount, priorView.summary.validSaleCount),
  } : null;

  return (
    <div className="sketch-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 className="font-display text-2xl text-ink">📊 Sales Report</h2>
          <p className="font-body text-ink/78" style={{ fontSize: '0.93rem' }}>
            {fmtDate(summary.dateStart)} — {fmtDate(summary.dateEnd)}
            {' · '}
            {summary.validSaleCount} sales
            {summary.refundCount > 0 && ` · ${summary.refundCount} refunded`}
            {range.preset !== 'all' && (
              <span style={{ color: '#1e3a8a', fontWeight: 600 }}>
                {' · '}{range.label} of {report.summary.validSaleCount} total
              </span>
            )}
            {daysOfWeek.length < 7 && (
              <span style={{ color: '#b45309', fontWeight: 600 }}>
                {' · '}{daysOfWeek.slice().sort().map(i => DOW_NAMES[i]).join(', ')} only
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowExport(v => !v)}
            style={{
              padding: '0.4rem 0.85rem',
              background: showExport ? '#1e3a8a' : '#fefdf8',
              color: showExport ? '#fefdf8' : '#1a1a2e',
              border: '1.5px solid #1a1a2e',
              borderRadius: '0.45rem',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '1px 2px 0 rgba(26, 26, 46, 0.15)',
            }}
          >
            {showExport ? '✕ Close' : '📥 Download'}
          </button>
          <button onClick={handleReset} style={resetBtnStyle()}>↻ New</button>
        </div>
      </div>

      {/* FILTER BAR */}
      <FilterBar
        presets={presets}
        range={range}
        setRangeFromPreset={setRangeFromPreset}
        customStart={customStart}
        customEnd={customEnd}
        setCustomStart={setCustomStart}
        setCustomEnd={setCustomEnd}
        applyCustomRange={applyCustomRange}
        dataMin={toIsoDate(report.summary.dateStart)}
        dataMax={toIsoDate(report.summary.dateEnd)}
        compare={compare}
        setCompare={setCompare}
        showCompare={range.preset !== 'all'}
        priorRange={priorView?.range}
        daysOfWeek={daysOfWeek}
        setDaysOfWeek={setDaysOfWeek}
      />

      {/* EXPORT PANEL — collapsible */}
      {showExport && (
        <div
          style={{
            padding: '0.95rem 1rem',
            background: 'rgba(30, 58, 138, 0.04)',
            border: '1.5px solid rgba(30, 58, 138, 0.2)',
            borderRadius: '0.625rem',
            marginBottom: '1.25rem',
          }}
        >
          <h3 style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.4rem' }}>
            📥 Download options
          </h3>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: 'rgba(26,26,46,0.78)', marginBottom: '0.4rem', lineHeight: 1.5 }}>
            Pick what to include in the Excel file, or download the visual report as an image.
          </p>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: 'rgba(26,26,46,0.7)', marginBottom: '0.75rem' }}>
            <strong>Filter applied:</strong> {range.label}
            {daysOfWeek.length < 7 && ` · ${daysOfWeek.slice().sort().map(i => DOW_NAMES[i]).join(', ')} only`}
            {' '}({summary.validSaleCount} sales)
          </p>

          {/* Trend granularity for export — defaults to current view */}
          <div style={{ marginBottom: '0.85rem' }}>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.74rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'rgba(26,26,46,0.7)',
              marginBottom: '0.35rem',
            }}>
              Trend sheet granularity
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}>
              {[
                { v: null,      label: 'Match view' },
                { v: 'day',     label: 'Day' },
                { v: 'week',    label: 'Week' },
                { v: 'month',   label: 'Month' },
                { v: 'quarter', label: 'Quarter' },
              ].map((o) => {
                const active = exportGranularity === o.v;
                return (
                  <button
                    key={o.v ?? 'auto'}
                    onClick={() => setExportGranularity(o.v)}
                    style={{
                      padding: '0.28rem 0.6rem',
                      background: active ? '#1e3a8a' : '#fefdf8',
                      color: active ? '#fefdf8' : '#1a1a2e',
                      border: `1.5px solid ${active ? '#1e3a8a' : 'rgba(26,26,46,0.18)'}`,
                      borderRadius: '0.35rem',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {o.label}
                  </button>
                );
              })}
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: 'rgba(26,26,46,0.6)' }}>
                {exportGranularity === null
                  ? `(currently viewing ${granularity})`
                  : `(viewing ${granularity}, exporting ${exportGranularity})`}
              </span>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.4rem',
            marginBottom: '0.85rem',
          }}>
            {[
              { key: 'includeSummary',  label: '📊 Summary stats',     desc: 'Key metrics' },
              { key: 'includeMonthly',  label: `📅 ${trendLabelFor(exportGranularity || granularity)} trend`, desc: 'Per-period breakdown' },
              { key: 'includeProducts', label: '🏆 Top products',      desc: 'Product-level data' },
              { key: 'includeAllSales', label: '📋 All sales (raw)',   desc: 'Every transaction' },
              { key: 'includeInsights', label: '💡 Insights',          desc: 'Text-based findings' },
            ].map(opt => (
              <label
                key={opt.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.45rem',
                  padding: '0.5rem 0.7rem',
                  background: exportOptions[opt.key] ? '#ffffff' : 'rgba(26,26,46,0.03)',
                  border: `1.5px solid ${exportOptions[opt.key] ? '#1e3a8a' : 'rgba(26,26,46,0.12)'}`,
                  borderRadius: '0.45rem',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                <input
                  type="checkbox"
                  checked={exportOptions[opt.key]}
                  onChange={() => toggleExportOption(opt.key)}
                  style={{ marginTop: '2px', accentColor: '#1e3a8a' }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.86rem', fontWeight: 600, color: '#1a1a2e' }}>
                    {opt.label}
                  </div>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', color: 'rgba(26,26,46,0.62)' }}>
                    {opt.desc}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={handleExcelExport} style={{
              padding: '0.5rem 1.1rem',
              background: '#047857',
              color: '#ffffff',
              border: '2px solid #1a1a2e',
              borderRadius: '0.5rem',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '0.92rem',
              cursor: 'pointer',
              boxShadow: '2px 3px 0 rgba(26, 26, 46, 0.18)',
            }}>
              📊 Download Excel (.xlsx)
            </button>
            <button onClick={handleImageExport} style={{
              padding: '0.5rem 1.1rem',
              background: '#fefdf8',
              color: '#1a1a2e',
              border: '2px solid #1a1a2e',
              borderRadius: '0.5rem',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.92rem',
              cursor: 'pointer',
              boxShadow: '2px 3px 0 rgba(26, 26, 46, 0.15)',
            }} title="Download the visual report (with charts) as a PNG image">
              🖼️ Download as image
            </button>
            {exportStatus && (
              <span style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.85rem',
                color: exportStatus.startsWith('⚠️') ? '#9f1239'
                     : exportStatus.startsWith('✓') ? '#047857'
                     : 'rgba(26,26,46,0.7)',
                fontWeight: 600,
              }}>
                {exportStatus}
              </span>
            )}
          </div>

          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: 'rgba(26,26,46,0.62)', marginTop: '0.7rem', lineHeight: 1.5 }}>
            ~ Excel reflects the active filter ({range.label}) and includes only what you've checked above ~
          </p>
        </div>
      )}

      {/* CAPTURABLE REPORT CONTENT — wrapped for image export */}
      <div id="etsy-report-content">

      {/* HEADLINE STATS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '0.65rem',
        marginBottom: '1.25rem',
      }}>
        <StatCard
          label="Revenue"
          value={fmt(summary.totalRevenue, sym)}
          color="#047857"
          sub={`${summary.totalQuantity} items sold`}
          delta={deltas?.revenue}
        />
        <StatCard
          label="Etsy fees"
          value={fmt(summary.totalFees, sym)}
          color="#9f1239"
          sub={`${summary.effectiveFeeRate.toFixed(1)}% of revenue${summary.feesAreEstimated ? ' · est.' : ''}`}
          delta={deltas?.fees}
          deltaInverted  // higher fees = bad
        />
        <StatCard
          label="Net profit"
          value={fmt(summary.totalProfit, sym)}
          color={summary.totalProfit >= 0 ? '#1e3a8a' : '#9f1239'}
          sub={`${summary.profitMargin.toFixed(1)}% margin`}
          delta={deltas?.profit}
        />
        <StatCard
          label="Avg order"
          value={fmt(summary.avgOrderValue, sym)}
          color="#7e22ce"
          sub={`${summary.uniqueOrders || summary.validSaleCount} orders`}
          delta={deltas?.aov}
        />
      </div>

      {/* PRIOR-PERIOD CONTEXT BANNER */}
      {priorView?.summary && (
        <div style={{
          padding: '0.6rem 0.85rem',
          background: 'rgba(30, 58, 138, 0.05)',
          border: '1px solid rgba(30, 58, 138, 0.2)',
          borderRadius: '0.5rem',
          marginBottom: '1.25rem',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.85rem',
          color: 'rgba(26,26,46,0.85)',
          lineHeight: 1.5,
        }}>
          <strong>Compared to {priorView.range.label}</strong> ({fmtDateShort(priorView.range.start)} – {fmtDateShort(priorView.range.end)}):
          {' '}prior revenue {fmt(priorView.summary.totalRevenue, sym)} · prior orders {priorView.summary.validSaleCount} · prior profit {fmt(priorView.summary.totalProfit, sym)}.
        </div>
      )}

      {/* INSIGHTS */}
      {insights.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 className="font-display" style={sectionHeaderStyle()}>💡 Key insights</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {insights.map((ins, i) => (
              <div key={i} style={{
                padding: '0.55rem 0.85rem',
                background: ins.icon === '⚠️'
                  ? 'rgba(180, 83, 9, 0.06)'
                  : ins.icon === '✓'
                    ? 'rgba(4, 120, 87, 0.06)'
                    : 'rgba(30, 58, 138, 0.04)',
                border: `1px solid ${ins.icon === '⚠️'
                  ? 'rgba(180, 83, 9, 0.25)'
                  : ins.icon === '✓'
                    ? 'rgba(4, 120, 87, 0.25)'
                    : 'rgba(30, 58, 138, 0.18)'}`,
                borderRadius: '0.45rem',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.92rem',
                color: 'rgba(26,26,46,0.88)',
                lineHeight: 1.5,
              }}>
                <span style={{ marginRight: '0.4rem' }}>{ins.icon}</span>
                {ins.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TREND CHART (now granularity-aware) */}
      {byBucket.length > 1 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.6rem',
            marginBottom: '0.6rem',
            flexWrap: 'wrap',
          }}>
            <h3 className="font-display" style={sectionHeaderStyle({ marginBottom: 0 })}>
              📅 Trend
              <span style={{
                marginLeft: '0.5rem',
                color: 'rgba(26,26,46,0.55)',
                fontWeight: 500,
                letterSpacing: '0.02em',
                textTransform: 'none',
                fontSize: '0.85rem',
              }}>
                ({byBucket.length} {granularity}{byBucket.length !== 1 ? 's' : ''})
              </span>
            </h3>
            <GranularitySwitch
              value={granularity}
              onChange={(g) => { setGranularity(g); setGranularityAuto(false); }}
            />
          </div>

          <div style={{
            padding: '0.85rem',
            background: '#ffffff',
            border: '1.5px solid rgba(26, 26, 46, 0.1)',
            borderRadius: '0.55rem',
            overflowX: 'auto',
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${byBucket.length}, minmax(${byBucket.length > 60 ? '20px' : byBucket.length > 30 ? '32px' : '48px'}, 1fr))`,
              gap: byBucket.length > 60 ? '0.15rem' : '0.35rem',
              alignItems: 'end',
              minHeight: '160px',
              minWidth: byBucket.length > 30 ? `${byBucket.length * 24}px` : 'auto',
            }}>
              {byBucket.map((m) => {
                const heightPct = (m.revenue / maxBucketRevenue) * 100;
                const showLabel = byBucket.length <= 30;
                return (
                  <div
                    key={m.bucket}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}
                    title={`${m.label} — Revenue ${fmt(m.revenue, sym)}, Profit ${fmt(m.profit, sym)}, ${m.orderCount} orders`}
                  >
                    {showLabel && (
                      <span className="font-numbers" style={{
                        fontSize: '0.7rem',
                        color: 'rgba(26,26,46,0.7)',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}>
                        {fmt(m.revenue, sym).replace(/\.\d+/, '')}
                      </span>
                    )}
                    <div style={{
                      width: '100%',
                      height: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                    }}>
                      <div style={{
                        width: '100%',
                        height: `${heightPct}%`,
                        background: 'linear-gradient(180deg, #047857 0%, #065f46 100%)',
                        borderRadius: '0.3rem 0.3rem 0 0',
                        position: 'relative',
                        minHeight: '4px',
                      }}>
                        {m.profit > 0 && m.revenue > 0 && (
                          <div style={{
                            position: 'absolute',
                            bottom: 0, left: 0, right: 0,
                            height: `${(m.profit / m.revenue) * 100}%`,
                            background: 'rgba(255,255,255,0.25)',
                            borderRadius: '0.3rem 0.3rem 0 0',
                          }} />
                        )}
                      </div>
                    </div>
                    <span style={{
                      fontSize: byBucket.length > 60 ? '0.6rem' : '0.68rem',
                      color: 'rgba(26,26,46,0.65)',
                      fontFamily: 'Inter, sans-serif',
                      whiteSpace: 'nowrap',
                      writingMode: byBucket.length > 30 ? 'vertical-rl' : 'horizontal-tb',
                      transform: byBucket.length > 30 ? 'rotate(180deg)' : 'none',
                    }}>
                      {m.label}
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '0.65rem',
              paddingTop: '0.55rem',
              borderTop: '1px dashed rgba(26,26,46,0.15)',
              fontSize: '0.78rem',
              color: 'rgba(26,26,46,0.7)',
              fontFamily: 'Inter, sans-serif',
              flexWrap: 'wrap',
            }}>
              <span>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#047857', borderRadius: '2px', marginRight: '0.3rem' }} />
                Revenue
              </span>
              <span>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(26,26,46,0.3)', borderRadius: '2px', marginRight: '0.3rem' }} />
                Profit (lighter band)
              </span>
              {granularityAuto && (
                <span style={{ marginLeft: 'auto', color: 'rgba(26,26,46,0.55)', fontStyle: 'italic' }}>
                  granularity auto-picked
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ALL PRODUCTS — scrollable area with search */}
      {topProducts.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '0.85rem',
            marginBottom: '0.6rem',
            flexWrap: 'wrap',
          }}>
            <h3 className="font-display" style={sectionHeaderStyle({ marginBottom: 0 })}>
              🏆 Products{' '}
              <span style={{
                color: 'rgba(26,26,46,0.55)',
                fontWeight: 500,
                letterSpacing: '0.02em',
                textTransform: 'none',
              }}>
                ({productSearch.trim()
                  ? `${filteredProducts.length} of ${topProducts.length}`
                  : `${topProducts.length} total`})
              </span>
            </h3>

            <div style={{ position: 'relative', flex: 1, minWidth: '180px', maxWidth: '320px' }}>
              <span aria-hidden="true" style={{
                position: 'absolute',
                left: '0.6rem',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '0.88rem',
                pointerEvents: 'none',
                opacity: 0.6,
              }}>
                🔍
              </span>
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search products…"
                style={{
                  width: '100%',
                  padding: '0.42rem 2rem 0.42rem 2rem',
                  border: '1.5px solid rgba(26,26,46,0.18)',
                  borderRadius: '0.42rem',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.88rem',
                  background: '#fefdf8',
                  color: '#1a1a2e',
                  outline: 'none',
                  transition: 'border-color 0.12s',
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#1e3a8a'}
                onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(26,26,46,0.18)'}
              />
              {productSearch && (
                <button
                  type="button"
                  onClick={() => setProductSearch('')}
                  aria-label="Clear search"
                  title="Clear search"
                  style={{
                    position: 'absolute',
                    right: '0.35rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(26,26,46,0.08)',
                    border: 'none',
                    borderRadius: '999px',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: 'rgba(26,26,46,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div style={{
            background: '#ffffff',
            border: '1.5px solid rgba(26, 26, 46, 0.1)',
            borderRadius: '0.55rem',
            maxHeight: '420px',
            overflowY: 'auto',
          }}>
            {filteredProducts.length === 0 ? (
              <div style={{
                padding: '2.5rem 1rem',
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif',
                color: 'rgba(26,26,46,0.65)',
              }}>
                <div style={{ fontSize: '1.6rem', marginBottom: '0.4rem' }}>🔍</div>
                <p style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  No products match "{productSearch}"
                </p>
                <button
                  onClick={() => setProductSearch('')}
                  style={{
                    fontSize: '0.85rem',
                    color: '#1e3a8a',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                    textDecoration: 'underline',
                    padding: 0,
                  }}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.88rem',
              }}>
                <thead style={{
                  position: 'sticky',
                  top: 0,
                  background: '#fefdf8',
                  zIndex: 1,
                  boxShadow: '0 1px 0 rgba(26,26,46,0.12)',
                }}>
                  <tr>
                    <th style={th()}>#</th>
                    <th style={{ ...th(), textAlign: 'left' }}>Product</th>
                    <th style={th()}>Qty</th>
                    <th style={th()}>Revenue</th>
                    <th style={th()}>Etsy fees</th>
                    <th style={th()}>Profit*</th>
                    {anyRefunds && <th style={th()}>Refunds</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p, i) => {
                    const pctOfTotal = summary.totalRevenue > 0
                      ? (p.revenue / summary.totalRevenue) * 100
                      : 0;
                    return (
                      <tr key={p.originalRank} style={{ borderTop: i > 0 ? '1px solid rgba(26,26,46,0.06)' : 'none' }}>
                        <td style={{ ...td(), color: 'rgba(26,26,46,0.62)' }}>{p.originalRank}</td>
                        <td style={{ ...td(), textAlign: 'left', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>
                          {p.name}
                          <div style={{ fontSize: '0.72rem', color: 'rgba(26,26,46,0.6)', marginTop: '0.1rem' }}>
                            {pctOfTotal.toFixed(1)}% of revenue
                          </div>
                        </td>
                        <td style={td()}>{p.quantity}</td>
                        <td style={{ ...td(), color: '#047857', fontWeight: 600 }}>{fmt(p.revenue, sym)}</td>
                        <td style={{ ...td(), color: '#9f1239' }}>{fmt(p.fees, sym)}</td>
                        <td style={{ ...td(), color: p.profit >= 0 ? '#1e3a8a' : '#9f1239', fontWeight: 600 }}>
                          {fmt(p.profit, sym)}
                        </td>
                        {anyRefunds && (
                          <td style={{ ...td(), color: p.refundCount > 0 ? '#9f1239' : 'rgba(26,26,46,0.5)' }}>
                            {p.refundCount > 0 ? p.refundCount : '—'}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <p className="font-note" style={{ fontSize: '0.8rem', color: 'rgba(26,26,46,0.62)', marginTop: '0.55rem', lineHeight: 1.5 }}>
            *Profit estimate excludes per-product material/labor cost, which isn't in the CSV. Use the calculator to add per-product cost data for true profit.
          </p>
        </div>
      )}

      {/* ADVANCED DETAILS — expandable */}
      <div style={{ marginTop: '1.5rem' }}>
        <button
          onClick={() => setShowAdvanced(v => !v)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '0.4rem 0',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.92rem',
            fontWeight: 600,
            color: '#1e3a8a',
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
          }}
        >
          {showAdvanced ? '▾' : '▸'} Advanced details
        </button>
        {showAdvanced && (() => {
          const sortedAmounts = (view.sales || [])
            .filter(s => !s.isRefund)
            .map(s => (s.itemTotal || 0) + (s.shipping || 0))
            .sort((a, b) => a - b);
          const median = sortedAmounts.length > 0
            ? sortedAmounts[Math.floor(sortedAmounts.length / 2)]
            : 0;
          const dayOfWeekCounts = [0, 0, 0, 0, 0, 0, 0];
          const dayOfWeekRev = [0, 0, 0, 0, 0, 0, 0];
          for (const sale of (view.sales || [])) {
            if (sale.isRefund || !sale.date) continue;
            const dow = sale.date.getDay();
            dayOfWeekCounts[dow]++;
            dayOfWeekRev[dow] += (sale.itemTotal || 0) + (sale.shipping || 0);
          }
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const bestDay = dayOfWeekRev.indexOf(Math.max(...dayOfWeekRev));

          const totalRev = summary.totalRevenue || 1;
          let hhi = 0;
          for (const p of topProducts) {
            const share = p.revenue / totalRev;
            hhi += share * share;
          }
          const diversityScore = Math.round((1 - hhi) * 100);

          return (
            <div style={{
              marginTop: '0.6rem',
              padding: '0.85rem 1rem',
              background: 'rgba(30, 58, 138, 0.03)',
              border: '1px solid rgba(30, 58, 138, 0.15)',
              borderRadius: '0.55rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '0.85rem',
            }}>
              <DetailItem label="Median order value" value={fmt(median, sym)} hint={`Half your orders are above this · avg is ${fmt(summary.avgOrderValue, sym)}`} />
              <DetailItem label="Repeat buyers" value={`${Math.round((summary.repeatBuyerRate || 0) * 100)}%`} hint={`${summary.uniqueBuyers} unique buyers across ${summary.uniqueOrders || summary.validSaleCount} orders`} />
              <DetailItem label="Best day of week" value={dayNames[bestDay]} hint={`${fmt(dayOfWeekRev[bestDay], sym)} total revenue · ${dayOfWeekCounts[bestDay]} orders`} />
              <DetailItem label="Catalog diversity" value={`${diversityScore} / 100`} hint={diversityScore < 40 ? 'Low — heavily concentrated' : diversityScore < 70 ? 'Moderate' : 'Healthy spread'} />
              <DetailItem label="Total discounts given" value={fmt(summary.totalDiscounts, sym)} hint={`${summary.totalRevenue > 0 ? ((summary.totalDiscounts / summary.totalRevenue) * 100).toFixed(1) : 0}% of revenue`} />
              <DetailItem label="Items per order" value={summary.validSaleCount > 0 ? (summary.totalQuantity / summary.validSaleCount).toFixed(2) : '—'} hint={`${summary.totalQuantity} items across ${summary.validSaleCount} orders`} />
            </div>
          );
        })()}
      </div>

      </div>{/* ─── end #etsy-report-content ─── */}

      {summary.feesAreEstimated && (
        <div style={{
          marginTop: '1rem',
          padding: '0.65rem 0.85rem',
          background: 'rgba(180, 83, 9, 0.05)',
          border: '1px dashed rgba(180, 83, 9, 0.3)',
          borderRadius: '0.45rem',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.85rem',
          color: 'rgba(26,26,46,0.78)',
          lineHeight: 1.55,
        }}>
          <strong>Note on fees:</strong> Your CSV doesn't include an "Order Net" column, so fee numbers are estimated using the calculator's settings ({settings?.country || 'US'} rates,{' '}
          {settings?.offsiteEnabled ? 'Offsite Ads on' : 'Offsite Ads off'}). For exact numbers, export a CSV that includes Order Net or check Etsy's monthly statement.
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────

function FilterBar({
  presets, range, setRangeFromPreset,
  customStart, customEnd, setCustomStart, setCustomEnd, applyCustomRange,
  dataMin, dataMax,
  compare, setCompare, showCompare, priorRange,
  daysOfWeek, setDaysOfWeek,
}) {
  const isCustom = range.preset === 'custom';
  const dowFilterActive = Array.isArray(daysOfWeek) && daysOfWeek.length > 0 && daysOfWeek.length < 7;

  const toggleDay = (idx) => {
    if (!setDaysOfWeek) return;
    const has = daysOfWeek.includes(idx);
    let next = has ? daysOfWeek.filter(d => d !== idx) : daysOfWeek.concat(idx).sort();
    // Don't allow zero-day selection — that means "no sales ever," which is silly.
    if (next.length === 0) next = ALL_DAYS;
    setDaysOfWeek(next);
  };

  const setDayPreset = (set) => {
    if (!setDaysOfWeek) return;
    setDaysOfWeek(set.slice());
  };

  return (
    <div style={{
      padding: '0.75rem 0.85rem',
      background: 'rgba(180, 83, 9, 0.04)',
      border: '1.5px solid rgba(180, 83, 9, 0.2)',
      borderRadius: '0.625rem',
      marginBottom: '1.25rem',
    }}>
      <div style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'rgba(26,26,46,0.65)',
        marginBottom: '0.45rem',
      }}>
        🗓️ Date range
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
        {presets.map((p) => (
          <button
            key={p.preset}
            onClick={() => setRangeFromPreset(p)}
            style={chipStyle(range.preset === p.preset)}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setRangeFromPreset({ preset: 'custom', label: 'Custom', start: null, end: null })}
          style={chipStyle(isCustom)}
        >
          📅 Custom…
        </button>
      </div>

      {isCustom && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem',
          padding: '0.55rem 0.7rem',
          background: 'rgba(255,255,255,0.55)',
          border: '1px dashed rgba(180,83,9,0.35)',
          borderRadius: '0.45rem',
          marginBottom: compare !== undefined ? '0.5rem' : 0,
        }}>
          <label style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            From
            <input
              type="date"
              value={customStart}
              min={dataMin}
              max={dataMax}
              onChange={(e) => setCustomStart(e.target.value)}
              style={dateInputStyle()}
            />
          </label>
          <label style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            to
            <input
              type="date"
              value={customEnd}
              min={dataMin}
              max={dataMax}
              onChange={(e) => setCustomEnd(e.target.value)}
              style={dateInputStyle()}
            />
          </label>
          <button
            onClick={applyCustomRange}
            disabled={!customStart || !customEnd}
            style={{
              padding: '0.35rem 0.85rem',
              background: (!customStart || !customEnd) ? 'rgba(26,26,46,0.15)' : '#1e3a8a',
              color: '#fefdf8',
              border: '1.5px solid #1a1a2e',
              borderRadius: '0.4rem',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: (!customStart || !customEnd) ? 'not-allowed' : 'pointer',
            }}
          >
            Apply
          </button>
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: 'rgba(26,26,46,0.6)' }}>
            (data covers {dataMin} → {dataMax})
          </span>
        </div>
      )}

      {/* DAYS OF WEEK — secondary filter */}
      {setDaysOfWeek && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.45rem',
          paddingTop: '0.5rem',
          marginTop: isCustom ? '0' : '0.25rem',
          borderTop: '1px dashed rgba(180,83,9,0.18)',
          marginBottom: showCompare ? '0.5rem' : 0,
        }}>
          <span style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'rgba(26,26,46,0.65)',
          }}>
            📆 Days
          </span>
          <div style={{ display: 'inline-flex', gap: '2px' }}>
            {DOW_NAMES.map((name, idx) => {
              const active = daysOfWeek.includes(idx);
              const isWeekend = idx === 0 || idx === 6;
              return (
                <button
                  key={idx}
                  onClick={() => toggleDay(idx)}
                  title={`${name}${active ? ' — click to exclude' : ' — click to include'}`}
                  style={{
                    width: '28px',
                    height: '28px',
                    padding: 0,
                    background: active ? (isWeekend ? '#b45309' : '#1e3a8a') : '#fefdf8',
                    color: active ? '#fefdf8' : 'rgba(26,26,46,0.6)',
                    border: `1.5px solid ${active ? (isWeekend ? '#b45309' : '#1e3a8a') : 'rgba(26,26,46,0.18)'}`,
                    borderRadius: '0.32rem',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                  }}
                >
                  {DOW_INITIALS[idx]}
                </button>
              );
            })}
          </div>
          <button onClick={() => setDayPreset(WEEKDAYS)} style={dayPresetStyle(daysOfWeek.length === 5 && WEEKDAYS.every(d => daysOfWeek.includes(d)))}>
            Weekdays
          </button>
          <button onClick={() => setDayPreset(WEEKENDS)} style={dayPresetStyle(daysOfWeek.length === 2 && WEEKENDS.every(d => daysOfWeek.includes(d)))}>
            Weekends
          </button>
          <button onClick={() => setDayPreset(ALL_DAYS)} style={dayPresetStyle(daysOfWeek.length === 7)}>
            All days
          </button>
          {dowFilterActive && (
            <span style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              color: '#1e3a8a',
              fontWeight: 600,
            }}>
              · only {daysOfWeek.slice().sort().map(i => DOW_NAMES[i]).join(', ')}
            </span>
          )}
        </div>
      )}

      {showCompare && (
        <label style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.85rem',
          color: '#1a1a2e',
          cursor: 'pointer',
          padding: '0.3rem 0.55rem',
          borderRadius: '0.35rem',
          background: compare ? 'rgba(30, 58, 138, 0.08)' : 'transparent',
          border: `1px solid ${compare ? 'rgba(30, 58, 138, 0.3)' : 'transparent'}`,
        }}>
          <input
            type="checkbox"
            checked={!!compare}
            onChange={(e) => setCompare(e.target.checked)}
            style={{ accentColor: '#1e3a8a' }}
          />
          Compare to prior period
          {compare && priorRange && (
            <span style={{ color: 'rgba(26,26,46,0.6)', fontWeight: 500 }}>
              (vs {priorRange.label})
            </span>
          )}
        </label>
      )}
    </div>
  );
}

function GranularitySwitch({ value, onChange }) {
  const opts = [
    { v: 'day',     label: 'Day' },
    { v: 'week',    label: 'Week' },
    { v: 'month',   label: 'Month' },
    { v: 'quarter', label: 'Quarter' },
  ];
  return (
    <div style={{
      display: 'inline-flex',
      gap: '2px',
      padding: '2px',
      background: 'rgba(26,26,46,0.06)',
      borderRadius: '0.45rem',
    }}>
      {opts.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          style={{
            padding: '0.3rem 0.65rem',
            background: value === o.v ? '#1e3a8a' : 'transparent',
            color: value === o.v ? '#fefdf8' : 'rgba(26,26,46,0.75)',
            border: 'none',
            borderRadius: '0.32rem',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function DetailItem({ label, value, hint }) {
  return (
    <div>
      <p style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.74rem',
        fontWeight: 700,
        color: 'rgba(26,26,46,0.7)',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        marginBottom: '0.15rem',
      }}>
        {label}
      </p>
      <p className="font-numbers" style={{
        fontSize: '1.1rem',
        fontWeight: 700,
        color: '#1a1a2e',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.1,
      }}>
        {value}
      </p>
      {hint && (
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.75rem',
          color: 'rgba(26,26,46,0.62)',
          marginTop: '0.15rem',
          lineHeight: 1.4,
        }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function StatCard({ label, value, color, sub, delta, deltaInverted }) {
  // Determine delta color: by default up=green, down=rose. Inverted swaps.
  let deltaColor = 'rgba(26,26,46,0.55)';
  let deltaSymbol = '→';
  if (delta && delta.dir === 'up') {
    deltaColor = deltaInverted ? '#9f1239' : '#047857';
    deltaSymbol = '↑';
  } else if (delta && delta.dir === 'down') {
    deltaColor = deltaInverted ? '#047857' : '#9f1239';
    deltaSymbol = '↓';
  }

  return (
    <div style={{
      padding: '0.7rem 0.85rem',
      background: '#ffffff',
      border: '1.5px solid rgba(26, 26, 46, 0.1)',
      borderRadius: '0.55rem',
      minWidth: 0,
    }}>
      <p style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.7rem',
        fontWeight: 700,
        color: 'rgba(26,26,46,0.7)',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        marginBottom: '0.2rem',
      }}>
        {label}
      </p>
      <p className="font-numbers" style={{
        fontSize: '1.45rem',
        fontWeight: 700,
        color,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.02em',
        lineHeight: 1.05,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </p>
      {sub && (
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.74rem',
          color: 'rgba(26,26,46,0.62)',
          marginTop: '0.15rem',
        }}>
          {sub}
        </p>
      )}
      {delta && (
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.78rem',
          fontWeight: 700,
          color: deltaColor,
          marginTop: '0.25rem',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {deltaSymbol}{' '}
          {delta.pct === null
            ? 'new'
            : Math.abs(delta.pct) < 0.5
              ? 'flat'
              : `${Math.abs(delta.pct).toFixed(1)}%`}
          <span style={{ color: 'rgba(26,26,46,0.5)', fontWeight: 500, marginLeft: '0.35rem' }}>
            vs prior
          </span>
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Style helpers
// ────────────────────────────────────────────────────────────────────
const th = () => ({
  padding: '0.5rem 0.7rem',
  textAlign: 'right',
  fontWeight: 700,
  fontSize: '0.78rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'rgba(26,26,46,0.7)',
});

const td = () => ({
  padding: '0.55rem 0.7rem',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
});

function chipStyle(active) {
  return {
    padding: '0.3rem 0.7rem',
    background: active ? '#1e3a8a' : '#fefdf8',
    color: active ? '#fefdf8' : '#1a1a2e',
    border: `1.5px solid ${active ? '#1e3a8a' : 'rgba(26,26,46,0.18)'}`,
    borderRadius: '0.4rem',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.83rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.12s',
  };
}

function dayPresetStyle(active) {
  return {
    padding: '0.22rem 0.55rem',
    background: active ? 'rgba(30, 58, 138, 0.12)' : 'transparent',
    color: active ? '#1e3a8a' : 'rgba(26,26,46,0.7)',
    border: `1px solid ${active ? 'rgba(30, 58, 138, 0.3)' : 'rgba(26,26,46,0.15)'}`,
    borderRadius: '0.32rem',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.76rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.12s',
  };
}

function dateInputStyle() {
  return {
    padding: '0.3rem 0.45rem',
    border: '1.5px solid rgba(26,26,46,0.2)',
    borderRadius: '0.35rem',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.85rem',
    background: '#fefdf8',
    color: '#1a1a2e',
  };
}

function resetBtnStyle() {
  return {
    padding: '0.4rem 0.85rem',
    background: '#fefdf8',
    border: '1.5px solid #1a1a2e',
    borderRadius: '0.45rem',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#1a1a2e',
    cursor: 'pointer',
    boxShadow: '1px 2px 0 rgba(26, 26, 46, 0.15)',
  };
}

function sectionHeaderStyle(extra = {}) {
  return {
    fontSize: '0.92rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(26,26,46,0.78)',
    marginBottom: '0.5rem',
    ...extra,
  };
}
